import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';

import { environment } from '../../../environments/environment';

export interface CompanyOption {
  id: string;
  name: string;
}

interface CompanyListResponse {
  content?: Array<{ id?: string; name?: string }>;
}

const STORAGE_COMPANY_ID_KEY = 'kobize.selectedCompanyId';
const STORAGE_YEAR_KEY = 'kobize.selectedYear';

/**
 * Globalny kontekst podmiotu (company) dla całej aplikacji kobize-web.
 * `currentCompanyId` / `selectedCompanyId$` są zsynchronizowane z `localStorage`
 * (`kobize.selectedCompanyId`). Zmiana aktywnej firmy z UI powinna być obserwowana
 * (np. nawigacja na listę raportów), żeby nie mieszać danych między podmiotami.
 */
@Injectable({ providedIn: 'root' })
export class CompanyContextService {
  private readonly companiesSubject = new BehaviorSubject<CompanyOption[]>([]);
  private readonly selectedCompanyIdSubject = new BehaviorSubject<string | null>(null);
  private readonly selectedYearSubject = new BehaviorSubject<number>(new Date().getFullYear());
  private readonly loadingSubject = new BehaviorSubject<boolean>(false);
  private readonly errorSubject = new BehaviorSubject<string | null>(null);

  /** Współdzielone wywołanie, żeby równoległe `loadCompanies()` nie dublowały HTTP. */
  private loadCompaniesInflight: Promise<void> | null = null;

  readonly companies$ = this.companiesSubject.asObservable();
  readonly selectedCompanyId$ = this.selectedCompanyIdSubject.asObservable();
  readonly selectedYear$ = this.selectedYearSubject.asObservable();
  readonly loading$ = this.loadingSubject.asObservable();
  readonly error$ = this.errorSubject.asObservable();

  constructor(private readonly http: HttpClient) {
    const storedYear = Number(localStorage.getItem(STORAGE_YEAR_KEY));
    if (!Number.isNaN(storedYear) && storedYear >= 2000 && storedYear <= 2100) {
      this.selectedYearSubject.next(storedYear);
    }
  }

  get currentCompanyId(): string | null {
    return this.selectedCompanyIdSubject.value;
  }

  async loadCompanies(force = false): Promise<void> {
    if (!force && this.companiesSubject.value.length > 0) {
      return;
    }
    if (this.loadCompaniesInflight != null) {
      return this.loadCompaniesInflight;
    }
    this.loadCompaniesInflight = (async () => {
      this.loadingSubject.next(true);
      this.errorSubject.next(null);
      try {
        const response = await firstValueFrom(
          this.http.get<CompanyListResponse>(`${environment.accountsApiUrl}/company/list`, {
            params: { page: '0', size: '500' }
          })
        );

        const companies = (response?.content ?? [])
          .filter((item): item is { id: string; name: string } => Boolean(item?.id) && Boolean(item?.name))
          .map((item) => ({ id: item.id, name: item.name }));

        this.companiesSubject.next(companies);
        this.restoreOrSelectDefaultCompany(companies);
      } catch (error) {
        this.errorSubject.next('companyContext.loadFailed');
        throw error;
      } finally {
        this.loadingSubject.next(false);
      }
    })();
    try {
      await this.loadCompaniesInflight;
    } finally {
      this.loadCompaniesInflight = null;
    }
  }

  selectCompany(companyId: string): void {
    this.selectedCompanyIdSubject.next(companyId);
    localStorage.setItem(STORAGE_COMPANY_ID_KEY, companyId);
  }

  setYear(year: number): void {
    this.selectedYearSubject.next(year);
    localStorage.setItem(STORAGE_YEAR_KEY, String(year));
  }

  private restoreOrSelectDefaultCompany(companies: CompanyOption[]): void {
    if (companies.length === 0) {
      this.selectedCompanyIdSubject.next(null);
      localStorage.removeItem(STORAGE_COMPANY_ID_KEY);
      return;
    }

    const storedCompanyId = localStorage.getItem(STORAGE_COMPANY_ID_KEY);
    const restored = storedCompanyId != null ? companies.find((c) => c.id === storedCompanyId) : undefined;
    const selected = restored ?? companies[0];
    this.selectCompany(selected.id);
  }
}
